/**
 * Small tables and text rules both clients render from, so the phone and
 * the web never drift: what each agent tool is called while it runs, the
 * timeline's filter chips, and how long text is shortened.
 */
import type { TimelineKind } from './timeline'

/** How a running tool reads in the thread. Unknown tools show their raw name. */
export const TOOL_VERBS: Record<string, string> = {
  write_artifact: 'Writing',
  read_artifacts: 'Reading',
  search: 'Searching',
  remember: 'Remembering',
  capture: 'Logging',
  schedule: 'Scheduling',
  analyze_image: 'Looking at',
  read_rubric: 'Reading the rubric',
  update_rubric: 'Updating the rubric',
  read_plan: 'Reading the plan',
  write_plan: 'Writing the plan',
  read_reviews: 'Reading the check-ins',
  write_review: 'Writing the review',
  read_sheet: 'Reading the sheets',
  mark_blocks: 'Marking the blocks',
  record_checkin: 'Recording the check-in',
  show: 'Showing',
  make_game: 'Making a game',
  retry_game: 'Trying it again',
  make_deck: 'Making cards',
  edit_deck: 'Changing the cards',
  read_knowledge: 'Checking what she knows',
  set_knowledge: 'Noting what she knows',
  open_path_step: 'Opening her reading step',
  read_app: 'Looking around',
  update_child: 'Updating her profile',
  archive_artifact: 'Putting it away',
  attach_artifact: 'Adding it to the activity',
}

/**
 * A finished tool step, in the past tense, for the tools whose running verb
 * would read as still going above what they made ("Making a game" over a
 * game that is done). It says what the step did, from what the tool
 * returned; where that game stands now is its card's to say. Anything else
 * keeps its running verb.
 */
export function toolDone(call: { name: string; status?: string; input?: any; output?: any }): string | undefined {
  if (call.status === 'running' || call.status === 'error') return undefined
  const out = call.output ?? {}
  if (out.error) return undefined
  if (call.name === 'make_game') {
    if (out.status === 'waiting') return 'Kept a change for the next version'
    if (out.version) return `Asked for version ${out.version}`
    return 'Started a game'
  }
  if (call.name === 'retry_game') return 'Tried it again'
  if (call.name === 'make_deck') return 'Started a deck'
  if (call.name === 'edit_deck') return 'Changed the cards'
  return undefined
}

export type TimelineFilterKey = 'all' | 'notes' | 'marks' | 'photos' | 'checkins' | 'progress' | 'made'

export const TIMELINE_FILTERS: Array<{ key: TimelineFilterKey; label: string; kinds: TimelineKind[] }> = [
  { key: 'all', label: 'Everything', kinds: [] },
  { key: 'notes', label: 'Notes', kinds: ['capture', 'note'] },
  { key: 'marks', label: 'Marked', kinds: ['mark'] },
  { key: 'photos', label: 'Photos', kinds: ['photo'] },
  { key: 'checkins', label: 'Check-ins', kinds: ['review', 'sapling'] },
  { key: 'progress', label: 'Moved', kinds: ['level'] },
  { key: 'made', label: 'Made', kinds: ['material'] },
]

/** Done / Partly done / Skipped, with "marked by" the same on both clients. */
export function markLabel(status: string | undefined, who?: string): string {
  const base = status === 'done' ? 'Done' : status === 'partial' ? 'Partly done' : status === 'skipped' ? 'Skipped' : ''
  if (!base) return ''
  return who ? `${base} · marked by ${who}` : base
}

/**
 * Sapling is on it: a reply is streaming, or the parent has just spoken and
 * the reply's row has not landed yet (it lands a moment after the message
 * does). A message long unanswered is not a reply still coming. `messages`
 * are one conversation's, oldest first.
 */
export function agentAnswering(messages: Array<{ role?: string; streaming?: boolean; createdAt?: number }>, now: number): boolean {
  if (messages.some((m) => m.streaming)) return true
  const last = messages[messages.length - 1]
  return last?.role === 'user' && now - (last.createdAt ?? 0) < 90_000
}

/**
 * A reply as it reads: runs of text with the cards the agent showed placed
 * where it showed them. A card with no position (or a parent's photo) goes
 * after the text.
 */
export type ReplyPiece = { text: string } | { cards: any[] }

export function splitAtCards(content: string, attachments?: any[]): ReplyPiece[] {
  const cards = (attachments ?? []).filter((a) => a?.kind === 'block' || (a?.kind === 'artifact' && typeof a.at === 'number'))
  const text = content ?? ''
  const cut = (a: any) => (typeof a.at === 'number' ? Math.min(Math.max(a.at, 0), text.length) : text.length)
  const stops = [...new Set(cards.map(cut))].sort((a, b) => a - b)
  const pieces: ReplyPiece[] = []
  let from = 0
  for (const stop of stops) {
    const run = text.slice(from, stop).trim()
    if (run) pieces.push({ text: run })
    pieces.push({ cards: cards.filter((a) => cut(a) === stop) })
    from = stop
  }
  const rest = text.slice(from).trim()
  if (rest) pieces.push({ text: rest })
  return pieces
}

/**
 * Text cut to at most `n` characters the way a person would: at the end of
 * a sentence when one finishes in the latter half, else at a word with an
 * ellipsis, never mid-word.
 */
export function clipText(text: string, n: number): string {
  const t = String(text ?? '').replace(/\s+/g, ' ').trim()
  if (t.length <= n) return t
  const head = t.slice(0, n)
  const sentence = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '), head.lastIndexOf('? '), /[.!?]$/.test(head) ? n - 1 : -1)
  if (sentence >= n / 2) return head.slice(0, sentence + 1)
  // Room for the ellipsis; a word cut through is dropped, one that ends at the cut is kept.
  const cut = t.slice(0, n - 1)
  const word = (/\s/.test(t[n - 1]!) ? cut : cut.replace(/\s+\S*$/, '')).replace(/[\s,;:]+$/, '')
  return `${word || cut}…`
}

/**
 * Letter sounds a label can start with ("m duck is the one to find", "sh
 * words first"). In a phonics app a capital M beside the m she hears is a
 * different thing, so a label that opens on one keeps its case. A and I are
 * words before they are sounds.
 */
const SOUND_TOKEN = /^\s*(?:[b-hj-z]|sh|ch|th|ng|ck|qu|ee|oo|ai|oa|ar|igh)(?=[\s,]|$)/

/** A name or label as the apps show it, whoever wrote it: first letter capital, the rest as written, unless it opens on a letter sound. */
export function sentenceCase(text: string): string {
  if (SOUND_TOKEN.test(text)) return text
  return text.replace(/^\s*(\p{Ll})/u, (_, c: string) => c.toUpperCase())
}
